<template>
  <div class="admin-container">
    <AdminSidebar :isOpen="isSidebarOpen" :userData="currentUser" />
    <div class="main-content" :class="{ 'content-shifted': isSidebarOpen }">
      <nav class="admin-nav">
        <button class="menu-btn" @click="toggleSidebar"><i class="fas fa-bars"></i></button>
      </nav>
      <div class="admin-content">
        <div class="invoices-container">
          <div class="header"><h1>Quản lý Hóa đơn điện tử</h1></div>
          <div class="filter-bar">
            <div class="input-group"><i class="fas fa-search"></i><input v-model="searchQuery" @keyup.enter="fetchInvoices()" placeholder="Tìm tên, SĐT, email, CCCD, group..." /></div>
            <div class="input-group"><i class="fas fa-calendar"></i><select v-model="dateField" @change="onDateFieldChange()"><option value="created">Ngày tạo</option><option value="booking">Ngày đặt</option><option value="issued">Ngày xuất</option></select></div>
            <div class="input-group"><i class="fas fa-clock"></i><select v-model="quickPreset" @change="onQuickPresetChange()"><option value="">Khoảng thời gian</option><option value="today">Hôm nay</option><option value="yesterday">Hôm qua</option><option value="last_7_days">7 ngày qua</option><option value="this_week">Tuần này</option><option value="last_week">Tuần trước</option><option value="this_month">Tháng này</option><option value="last_month">Tháng trước</option></select></div>
            <div class="input-group"><i class="fas fa-calendar"></i><input type="date" v-model="fromDate" @change="onFromDateChange()" /></div>
            <div class="input-group"><i class="fas fa-calendar"></i><input type="date" v-model="toDate" @change="fetchInvoices()" /></div>
          </div>
          <div class="invoice-toolbar">
            <div class="toolbar-row">
              <div class="input-group"><i class="fas fa-search"></i><input v-model="newGroupId" placeholder="Booking Group ID..." /></div>
              <button @click="createInvoice" :disabled="creating" class="btn-add"><i :class="creating ? 'fas fa-spinner fa-spin' : 'fas fa-plus'"></i><span class="btn-label">{{ creating ? 'Đang tạo...' : 'Tạo hóa đơn' }}</span></button>
            </div>
            <div class="toolbar-row">
              <div class="input-group"><i class="fas fa-calendar"></i><input type="date" v-model="syncFromDate" /></div>
              <div class="input-group"><i class="fas fa-calendar"></i><input type="date" v-model="syncToDate" /></div>
              <button @click="syncInvoices" :disabled="syncing" class="btn-sync"><i :class="syncing ? 'fas fa-spinner fa-spin' : 'fas fa-sync'"></i><span class="btn-label">{{ syncing ? 'Đang đồng bộ...' : 'Đồng bộ' }}</span></button>
            </div>
          </div>
          <div class="status-tabs-wrap">
            <div class="status-tabs">
              <button v-for="tab in tabs" :key="tab.value" @click="currentStatus = tab.value; page = 1; fetchInvoices()" :class="['tab-btn', { active: currentStatus === tab.value }]">{{ tab.label }}</button>
            </div>
          </div>
          <div class="table-container">
            <div v-if="loading" class="table-loading"><div class="table-spinner"></div><p>Đang tải...</p></div>
            <div v-else-if="invoices.length === 0" class="table-empty"><i class="fas fa-file-invoice table-empty-icon"></i><h3>Không có hóa đơn nào</h3></div>
            <div v-else class="table-responsive">
              <table class="admin-table desktop-only">
                <thead><tr><th>ID</th><th>Nhóm</th><th>Người mua</th><th class="text-right">Tổng</th><th>Trạng thái</th><th>Ngày đặt</th><th>Ngày xuất</th><th>Ngày tạo</th><th></th></tr></thead>
                <tbody>
                  <tr v-for="inv in invoices" :key="inv.invoice_request_id">
                    <td class="nowrap">#{{ inv.invoice_request_id }}</td>
                    <td class="mono-sm">{{ inv.booking_group_id?.slice(-8) }}</td>
                    <td class="max-w-48">{{ inv.buyer_name }}</td>
                    <td class="text-right nowrap">{{ formatMoney(inv.total_amount) }}</td>
                    <td><span :class="'badge badge-' + inv.status">{{ statusLabel(inv.status) }}</span></td>
                    <td class="nowrap">{{ formatDate(inv.booking_date) }}</td>
                    <td class="nowrap">{{ inv.status === 'issued' && inv.issued_date ? formatDate(inv.issued_date) : '—' }}</td>
                    <td class="nowrap">{{ formatDate(inv.created_at) }}</td>
                    <td><button @click="openDetail(inv)" class="action-btn"><i class="fas fa-eye"></i></button></td>
                  </tr>
                </tbody>
              </table>
              <div class="mobile-only">
                <div v-for="inv in invoices" :key="'m-'+inv.invoice_request_id" class="mobile-card" @click="openDetail(inv)">
                  <div class="mc-top"><strong>#{{ inv.invoice_request_id }}</strong><span :class="'badge badge-' + inv.status">{{ statusLabel(inv.status) }}</span></div>
                  <div class="mc-name">{{ inv.buyer_name }}</div>
                  <div class="mc-meta">{{ formatMoney(inv.total_amount) }} &middot; Đặt: {{ formatDate(inv.booking_date) }}<template v-if="inv.status === 'issued' && inv.issued_date"> &middot; Xuất: {{ formatDate(inv.issued_date) }}</template> &middot; Tạo: {{ formatDate(inv.created_at) }}</div>
                </div>
              </div>
            </div>
          </div>
          <div class="pagination" v-if="totalPages > 1">
            <button class="pagination-btn" :disabled="page <= 1" @click="page--; fetchInvoices()">Trước</button>
            <span class="page-info">Trang {{ page }} / {{ totalPages }}</span>
            <button class="pagination-btn" :disabled="page >= totalPages" @click="page++; fetchInvoices()">Sau</button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal" v-if="selectedInvoice" @click.self="selectedInvoice = null">
      <div class="modal-content modal-responsive">
        <div class="modal-header"><h2>Hóa đơn #{{ selectedInvoice.invoice_request_id }}</h2><button class="close-btn" @click="selectedInvoice = null">&times;</button></div>
        <div class="modal-body">
          <fieldset :disabled="!canEdit" class="modal-fieldset">
            <legend>Thông tin người mua</legend>
            <div class="buyer-grid">
              <div class="form-group"><label>Tên</label><input v-model="editForm.buyer_name" class="form-control" /></div>
              <div class="form-group"><label>MST</label><input v-model="editForm.buyer_tax_code" class="form-control" /></div>
              <div class="form-group"><label>CCCD</label><input v-model="editForm.buyer_national_id" class="form-control" /></div>
              <div class="form-group"><label>Email</label><input v-model="editForm.buyer_email" class="form-control" /></div>
              <div class="form-group"><label>ĐT</label><input v-model="editForm.buyer_phone" class="form-control" /></div>
              <div class="form-group"><label>Loại</label><select v-model="editForm.buyer_type" class="form-control"><option value="personal">Cá nhân</option><option value="company">Công ty</option></select></div>
              <div class="form-group form-full"><label>Địa chỉ</label><input v-model="editForm.buyer_address" class="form-control" /></div>
            </div>
          </fieldset>
          <div class="table-scroll">
            <table class="admin-table">
              <thead><tr><th>#</th><th>Tên</th><th>ĐVT</th><th class="text-right">SL</th><th class="text-right">Đơn giá</th><th>Thuế</th><th class="text-right">Thành tiền</th></tr></thead>
              <tbody>
                <tr v-for="item in editForm.items" :key="item.line_number">
                  <td>{{ item.line_number }}</td><td>{{ item.item_name }}</td><td>{{ item.unit }}</td>
                  <td class="text-right">{{ item.quantity }}</td><td class="text-right nowrap">{{ formatMoney(item.unit_price) }}</td>
                  <td>{{ item.tax_rate }}%</td><td class="text-right nowrap">{{ formatMoney(item.total_amount) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="invoice-totals">
            <div class="t-row"><span>Trước thuế</span><strong>{{ formatMoney(editForm.total_before_tax) }}</strong></div>
            <div class="t-row"><span>Thuế 8%</span><strong>{{ formatMoney(editForm.tax_amount) }}</strong></div>
            <div class="t-row t-final"><span>Tổng</span><strong>{{ formatMoney(editForm.total_amount) }}</strong></div>
          </div>
          <div v-if="selectedInvoice.explain && selectedInvoice.explain.discount_total > 0" class="explain-box">
            <div class="explain-header">
              <h3>Giải thích giảm giá</h3>
              <span class="explain-badge">Nội bộ</span>
            </div>
            <table class="admin-table explain-table">
              <thead>
                <tr>
                  <th>Tên</th><th class="text-right">SL</th>
                  <th class="text-right">Giá gốc</th><th class="text-right">Giá sau giảm</th>
                  <th class="text-right">% giảm</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(line, idx) in selectedInvoice.explain.ticket_lines" :key="idx">
                  <td>{{ line.item_name }}</td>
                  <td class="text-right">{{ line.quantity }}</td>
                  <td class="text-right nowrap">{{ formatMoney(line.unit_price_gross) }}</td>
                  <td class="text-right nowrap">{{ formatMoney(line.unit_price_after_discount) }}</td>
                  <td class="text-right nowrap" style="color: #059669">−{{ line.discount_percent.toFixed(1) }}%</td>
                </tr>
              </tbody>
            </table>
            <div class="explain-summary">
              <div class="t-row"><span>Tổng trước giảm (gồm thuế)</span><strong>{{ formatMoney(selectedInvoice.explain.gross_amount) }}</strong></div>
              <div v-for="d in selectedInvoice.explain.discount_lines" :key="d.code" class="t-row" style="color: #059669">
                <span>Mã {{ d.code }}<template v-if="d.discount_type === 'percentage'"> <small style="color: #64748b">(−{{ d.discount_value }}%<template v-if="d.max_discount">, tối đa {{ formatMoney(d.max_discount) }}</template>)</small></template></span>
                <strong>−{{ formatMoney(d.amount) }}</strong>
              </div>
              <div class="t-row t-final"><span>Còn lại (= tổng hóa đơn)</span><strong>{{ formatMoney(selectedInvoice.explain.net_amount) }}</strong></div>
            </div>
          </div>
          <div v-if="selectedInvoice.status === 'issued'" class="success-box"><strong>Số: {{ selectedInvoice.invoice_number }}</strong><div class="flex gap-2 mt-2"><a v-if="selectedInvoice.invoice_pdf_url" :href="selectedInvoice.invoice_pdf_url" target="_blank" class="action-btn"><i class="fas fa-file-pdf"></i> PDF</a><a v-if="selectedInvoice.invoice_xml_url" :href="selectedInvoice.invoice_xml_url" target="_blank" class="action-btn"><i class="fas fa-file-code"></i> XML</a></div></div>
          <div v-if="selectedInvoice.status === 'failed'" class="error-box"><i class="fas fa-exclamation-triangle"></i> {{ selectedInvoice.sepay_message }}</div>
        </div>
        <div class="modal-footer">
          <button v-if="canEdit" @click="saveEdits" class="btn btn-secondary"><i class="fas fa-save"></i> Lưu</button>
          <button v-if="canEdit" @click="draft" class="btn btn-secondary"><i class="fas fa-file-alt"></i> Xuất nháp</button>
          <button v-if="canEdit" @click="approve" class="btn btn-success"><i class="fas fa-check"></i> Duyệt</button>
          <button v-if="canEdit" @click="reject" class="btn btn-danger"><i class="fas fa-times"></i> Từ chối</button>
          <button v-if="selectedInvoice.status === 'failed'" @click="retry" class="btn btn-warning"><i class="fas fa-redo"></i> Thử lại</button>
          <button @click="selectedInvoice = null" class="btn btn-secondary">Đóng</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AdminSidebar from '@/components/common/AdminSidebar.vue'
import axios from '../../plugins/axios'
import { ToastService } from '@/services/ToastService'
export default {
  name: 'AdminInvoices',
  components: { AdminSidebar },
  data() { return { isSidebarOpen: true, currentUser: {}, invoices: [], loading: false, currentStatus: 'pending', page: 1, perPage: 20, totalPages: 1, newGroupId: '', syncFromDate: '', syncToDate: '', syncing: false, creating: false, searchQuery: '', fromDate: '', toDate: '', dateField: 'created', quickPreset: '', tabs: [{ label: 'Chờ duyệt', value: 'pending' }, { label: 'Đã duyệt', value: 'approved' }, { label: 'Đã xuất', value: 'issued' }, { label: 'Từ chối', value: 'rejected' }, { label: 'Lỗi', value: 'failed' }, { label: 'Tất cả', value: 'all' }], selectedInvoice: null, editForm: {} } },
  computed: { canEdit() { return this.selectedInvoice && ['pending', 'failed'].includes(this.selectedInvoice.status) } },
  mounted() { this.loadUser(); this.fetchInvoices() },
  methods: {
    loadUser() { try { const t = localStorage.getItem('adminToken'); if (t) this.currentUser = JSON.parse(atob(t.split('.')[1])) } catch (e) {} },
    toggleSidebar() { this.isSidebarOpen = !this.isSidebarOpen },
    onDateFieldChange() { if (this.dateField === 'issued' && this.currentStatus === 'pending') { this.currentStatus = 'issued' } this.page = 1; this.fetchInvoices() },
    onFromDateChange() { if (this.fromDate && !this.toDate) { this.toDate = this.fromDate } this.page = 1; this.fetchInvoices() },
    onQuickPresetChange() {
      const now = new Date(); const formatDateStr = (d) => { const yyyy = d.getFullYear(); const mm = String(d.getMonth() + 1).padStart(2, '0'); const dd = String(d.getDate()).padStart(2, '0'); return `${yyyy}-${mm}-${dd}` }
      if (this.quickPreset === 'today') { this.fromDate = formatDateStr(now); this.toDate = formatDateStr(now) }
      else if (this.quickPreset === 'yesterday') { const y = new Date(now); y.setDate(y.getDate() - 1); this.fromDate = formatDateStr(y); this.toDate = formatDateStr(y) }
      else if (this.quickPreset === 'last_7_days') { const start = new Date(now); start.setDate(start.getDate() - 6); this.fromDate = formatDateStr(start); this.toDate = formatDateStr(now) }
      else if (this.quickPreset === 'this_week') { const start = new Date(now); const day = start.getDay(); const diff = start.getDate() - day + (day === 0 ? -6 : 1); start.setDate(diff); this.fromDate = formatDateStr(start); this.toDate = formatDateStr(now) }
      else if (this.quickPreset === 'last_week') { const start = new Date(now); const day = start.getDay(); const diffToMon = start.getDate() - day + (day === 0 ? -6 : 1); const lastMon = new Date(start); lastMon.setDate(diffToMon - 7); const lastSun = new Date(lastMon); lastSun.setDate(lastMon.getDate() + 6); this.fromDate = formatDateStr(lastMon); this.toDate = formatDateStr(lastSun) }
      else if (this.quickPreset === 'this_month') { const start = new Date(now.getFullYear(), now.getMonth(), 1); this.fromDate = formatDateStr(start); this.toDate = formatDateStr(now) }
      else if (this.quickPreset === 'last_month') { const start = new Date(now.getFullYear(), now.getMonth() - 1, 1); const end = new Date(now.getFullYear(), now.getMonth(), 0); this.fromDate = formatDateStr(start); this.toDate = formatDateStr(end) }
      else { this.fromDate = ''; this.toDate = '' }
      this.page = 1; this.fetchInvoices()
    },
    async fetchInvoices() { this.loading = true; try { const r = await axios.get('/admin/invoice-requests', { params: { page: this.page, per_page: this.perPage, status_filter: this.currentStatus, search: this.searchQuery || undefined, from_date: this.fromDate || undefined, to_date: this.toDate || undefined, date_field: this.dateField } }); this.invoices = r.data.items; this.totalPages = r.data.pages } catch (e) { console.error(e) } finally { this.loading = false } },
    async openDetail(inv) { try { const r = await axios.get(`/admin/invoice-requests/${inv.invoice_request_id}`); this.selectedInvoice = r.data; this.editForm = { buyer_name: r.data.buyer_name, buyer_tax_code: r.data.buyer_tax_code, buyer_national_id: r.data.buyer_national_id, buyer_email: r.data.buyer_email, buyer_phone: r.data.buyer_phone, buyer_address: r.data.buyer_address, buyer_type: r.data.buyer_type, payment_method: r.data.payment_method, issued_date: r.data.issued_date, notes: r.data.notes, items: r.data.items || [], total_before_tax: r.data.total_before_tax, tax_amount: r.data.tax_amount, total_amount: r.data.total_amount } } catch (e) { console.error(e) } },
    async syncInvoices() {
      if (this.syncing) return;
      this.syncing = true;
      try {
        const params = [this.syncFromDate && 'from_date=' + this.syncFromDate, this.syncToDate && 'to_date=' + this.syncToDate].filter(Boolean).join('&');
        const q = params ? '?' + params : '';
        await axios.post('/admin/invoice-requests/sync' + q);
        ToastService.success('Đang đồng bộ nền, vui lòng đợi...');
        this.pollSync();
      } catch (e) {
        ToastService.error('Lỗi: ' + (e.response?.data?.detail || e.message));
        this.syncing = false;
      }
    },
    async pollSync() {
      let stable = 0, last = -1, tries = 0;
      const check = async () => {
        tries++;
        try {
          const r = await axios.get('/admin/invoice-requests', { params: { page: 1, per_page: 1, status_filter: this.currentStatus } });
          const total = r.data.total;
          if (total === last) { stable++; } else { stable = 0; last = total; }
          if (stable >= 2 || tries >= 20) {
            this.syncing = false;
            this.fetchInvoices();
            ToastService.success('Đồng bộ xong! Tổng ' + total + ' hóa đơn.');
            return;
          }
        } catch (e) { /* keep polling */ }
        setTimeout(check, 3000);
      };
      setTimeout(check, 3000);
    },
    async createInvoice() { if (this.creating) return; this.creating = true; if (!this.newGroupId.trim()) { ToastService.error('Nhập Booking Group ID'); this.creating = false; return } try { await axios.post('/admin/invoice-requests/create', { booking_group_id: this.newGroupId.trim() }); this.newGroupId = ''; this.fetchInvoices(); ToastService.success('Đã tạo hóa đơn!') } catch (e) { ToastService.error('Lỗi: ' + (e.response?.data?.detail || e.message)) } finally { this.creating = false } },
    async saveEdits() { try { await axios.put(`/admin/invoice-requests/${this.selectedInvoice.invoice_request_id}`, this.editForm); await this.openDetail(this.selectedInvoice); this.fetchInvoices() } catch (e) { ToastService.error(e.response?.data?.detail || e.message) } },
    async approve() { if (!confirm('Duyệt xuất?')) return; try { await axios.post(`/admin/invoice-requests/${this.selectedInvoice.invoice_request_id}/approve`); await this.openDetail(this.selectedInvoice); this.fetchInvoices() } catch (e) { ToastService.error(e.response?.data?.detail || e.message) } },
    async reject() { const r = prompt('Lý do:'); if (r === null) return; try { await axios.post(`/admin/invoice-requests/${this.selectedInvoice.invoice_request_id}/reject`, { notes: r }); await this.openDetail(this.selectedInvoice); this.fetchInvoices() } catch (e) { ToastService.error(e.response?.data?.detail || e.message) } },
    async retry() { if (!confirm('Thử lại?')) return; try { await axios.post(`/admin/invoice-requests/${this.selectedInvoice.invoice_request_id}/retry`); await this.openDetail(this.selectedInvoice); this.fetchInvoices() } catch (e) { ToastService.error(e.response?.data?.detail || e.message) } },
    async draft() { try { const r = await axios.post(`/admin/invoice-requests/${this.selectedInvoice.invoice_request_id}/draft`); if (r.data.pdf_url) { window.open(r.data.pdf_url, '_blank') } else { ToastService.success(r.data.message) } } catch (e) { ToastService.error(e.response?.data?.detail || e.message) } },
    formatMoney(v) {
      if (v == null) return ''
      const num = Number(v)
      return (num % 1 === 0 
        ? num.toLocaleString('vi-VN') 
        : num.toLocaleString('vi-VN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      ) + 'đ'
    },
    formatDate(v) { return v ? new Date(v).toLocaleDateString('vi-VN') : '' },
    statusLabel(s) { return { pending: 'Chờ duyệt', approved: 'Đã duyệt', issued: 'Đã xuất', rejected: 'Từ chối', failed: 'Lỗi' }[s] || s },
  },
}
</script>

<style scoped>
.invoices-container .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; flex-wrap: wrap; gap: 16px; }
.invoices-container .header h1 { font-size: 1.5rem; font-weight: 600; color: #283e37; margin: 0; }
.invoice-toolbar { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 16px; }
.toolbar-row { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
.filter-bar { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 16px; }
.input-group { display: flex; align-items: center; gap: 8px; padding: 9px 14px; border: 1px solid #e2e8f0; border-radius: 10px; background: #fff; transition: all .2s; }
.input-group:focus-within { border-color: #283e37; box-shadow: 0 0 0 3px rgba(40,62,55,.1); }
.input-group i { color: #a0aec0; font-size: 14px; }
.input-group input { border: none; outline: none; font-size: 14px; min-width: 0; width: 150px; background: transparent; color: #2d3748; }
.btn-add, .btn-sync { display: inline-flex; align-items: center; gap: 6px; padding: 9px 18px; border-radius: 10px; font-size: 14px; font-weight: 500; cursor: pointer; white-space: nowrap; border: none; transition: all .2s; }
.btn-add { background: #283e37; color: #fff; box-shadow: 0 4px 14px 0 rgba(40,62,55,.25); }
.btn-add:hover:not(:disabled) { background: #1f312c; transform: translateY(-1px); }
.btn-add:disabled, .btn-sync:disabled { opacity: .6; cursor: not-allowed; }
.btn-sync { background: #fff; color: #283e37; border: 1px solid #283e37; }
.btn-sync:hover:not(:disabled) { background: #e8f0ed; }
.status-tabs-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; margin-bottom: 16px; padding-bottom: 4px; }
.status-tabs { display: flex; gap: 8px; min-width: max-content; }
.tab-btn { padding: 8px 16px; border: 1px solid #e2e8f0; border-radius: 10px; background: #fff; cursor: pointer; font-size: 13px; font-weight: 500; color: #4a5568; white-space: nowrap; flex-shrink: 0; transition: all .2s; }
.tab-btn:hover { border-color: #283e37; color: #283e37; }
.tab-btn.active { background: #283e37; color: #fff; border-color: #283e37; box-shadow: 0 4px 14px 0 rgba(40,62,55,.2); }
.table-container { background: #fff; border-radius: 16px; box-shadow: 0 1px 3px 0 rgba(0,0,0,.1); overflow: hidden; border: 1px solid #edf2f7; }
.admin-table { width: 100%; border-collapse: collapse; }
.admin-table thead { background: #f7fafc; }
.admin-table th { padding: 14px 16px; text-align: left; font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .03em; color: #718096; border-bottom: 1px solid #e2e8f0; }
.admin-table td { padding: 14px 16px; font-size: 14px; color: #2d3748; border-bottom: 1px solid #edf2f7; }
.admin-table tbody tr { transition: background .15s; }
.admin-table tbody tr:hover { background: #f7fafc; }
.admin-table tbody tr:last-child td { border-bottom: none; }
.text-right { text-align: right; }
.desktop-only { display: none; }
.mobile-only { display: block; }
@media (min-width: 768px) { .desktop-only { display: table; } .mobile-only { display: none; } }
.nowrap { white-space: nowrap; }
.mono-sm { font-family: 'SF Mono', Monaco, monospace; font-size: 12px; color: #718096; }
.max-w-48 { max-width: 12rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 500; }
.table-loading, .table-empty { text-align: center; padding: 60px 20px; color: #a0aec0; }
.table-spinner { width: 36px; height: 36px; border: 3px solid #e2e8f0; border-top-color: #283e37; border-radius: 50%; animation: spin .8s linear infinite; margin: 0 auto 12px; }
.table-empty-icon { font-size: 48px; color: #cbd5e0; margin-bottom: 12px; }
.table-empty h3 { color: #4a5568; font-size: 16px; margin: 0; }
@keyframes spin { to { transform: rotate(360deg); } }
.mobile-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; margin-bottom: 10px; cursor: pointer; transition: all .2s; }
.mobile-card:active { background: #f7fafc; transform: scale(.99); }
.mc-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
.mc-top strong { color: #283e37; font-size: 15px; }
.mc-name { font-size: 15px; font-weight: 600; color: #2d3748; margin-bottom: 4px; }
.mc-meta { font-size: 13px; color: #718096; }
.badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 11px; font-weight: 600; flex-shrink: 0; }
.badge-pending { background: #fef3c7; color: #92400e; }
.badge-approved { background: #dbeafe; color: #1e40af; }
.badge-issued { background: #d1fae5; color: #065f46; }
.badge-rejected { background: #fee2e2; color: #991b1b; }
.badge-failed { background: #fed7aa; color: #9a3412; }
.action-btn { display: inline-flex; align-items: center; gap: 6px; padding: 7px 12px; border: 1px solid #e2e8f0; border-radius: 8px; background: #fff; cursor: pointer; font-size: 13px; color: #283e37; text-decoration: none; transition: all .2s; }
.action-btn:hover { background: #e8f0ed; border-color: #283e37; }
.pagination { display: flex; align-items: center; justify-content: center; gap: 16px; margin-top: 20px; }
.pagination-btn { padding: 9px 18px; border: 1px solid #e2e8f0; border-radius: 10px; background: #fff; cursor: pointer; font-size: 14px; color: #283e37; transition: all .2s; }
.pagination-btn:hover:not(:disabled) { background: #e8f0ed; border-color: #283e37; }
.pagination-btn:disabled { opacity: .4; cursor: not-allowed; }
.page-info { font-size: 14px; color: #718096; font-weight: 500; }
.modal { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 16px; }
.modal-content { background: #fff; border-radius: 16px; box-shadow: 0 20px 25px -5px rgba(0,0,0,.1); }
.modal-responsive { width: 95%; max-width: 900px; max-height: 90vh; overflow-y: auto; }
@media (max-width: 767px) { .modal-responsive { width: 100%; height: 100vh; max-height: 100vh; border-radius: 0; } }
.modal-header { display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; border-bottom: 1px solid #edf2f7; position: sticky; top: 0; background: #fff; z-index: 1; }
.modal-header h2 { font-size: 18px; font-weight: 600; color: #283e37; margin: 0; }
.close-btn { background: none; border: none; font-size: 28px; line-height: 1; color: #a0aec0; cursor: pointer; transition: color .2s; }
.close-btn:hover { color: #2d3748; }
.modal-body { padding: 24px; }
.modal-footer { display: flex; flex-wrap: wrap; gap: 8px; padding: 16px 24px; border-top: 1px solid #edf2f7; position: sticky; bottom: 0; background: #fff; }
.modal-fieldset { border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; margin-bottom: 16px; }
.modal-fieldset legend { font-weight: 600; font-size: 14px; color: #283e37; padding: 0 8px; }
.buyer-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
@media (max-width: 480px) { .buyer-grid { grid-template-columns: 1fr; } }
.form-group { display: flex; flex-direction: column; }
.form-group label { font-size: 12px; color: #718096; margin-bottom: 4px; font-weight: 500; }
.form-full { grid-column: 1 / -1; }
.form-control { padding: 9px 12px; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 14px; width: 100%; box-sizing: border-box; transition: all .2s; }
.form-control:focus { outline: none; border-color: #283e37; box-shadow: 0 0 0 3px rgba(40,62,55,.1); }
.table-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; margin: 12px 0; border: 1px solid #edf2f7; border-radius: 12px; }
.invoice-totals { background: #f7fafc; border-radius: 12px; padding: 16px; margin-top: 16px; }
.t-row { display: flex; justify-content: space-between; padding: 6px 0; font-size: 14px; color: #4a5568; }
.t-final { border-top: 2px solid #e2e8f0; margin-top: 8px; padding-top: 10px; font-size: 17px; font-weight: 700; color: #283e37; }
.success-box { background: #d1fae5; border: 1px solid #6ee7b7; border-radius: 12px; padding: 16px; margin-top: 16px; color: #065f46; }
.error-box { background: #fee2e2; border: 1px solid #fca5a5; border-radius: 12px; padding: 16px; margin-top: 16px; color: #991b1b; }
.btn { padding: 9px 18px; border: 1px solid #e2e8f0; border-radius: 10px; cursor: pointer; background: #fff; font-size: 14px; font-weight: 500; transition: all .2s; }
.btn-secondary { background: #f7fafc; color: #4a5568; }
.btn-secondary:hover { background: #edf2f7; }
.btn-success { background: #48bb78; color: #fff; border-color: #48bb78; }
.btn-success:hover { background: #38a169; }
.btn-danger { background: #e53e3e; color: #fff; border-color: #e53e3e; }
.btn-danger:hover { background: #c53030; }
.btn-warning { background: #ed8936; color: #fff; border-color: #ed8936; }
.btn-warning:hover { background: #dd6b20; }
.flex { display: flex; }
.gap-2 { gap: 8px; }
.mt-2 { margin-top: 8px; }
@media (max-width: 767px) { .btn-label { display: none; } }

.explain-box {
  border: 2px solid #6ee7b7;
  border-radius: 12px;
  padding: 16px;
  margin-top: 16px;
  background: #f0fdf4;
}

.explain-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 800;
  text-transform: uppercase;
}

.explain-badge {
  font-size: 11px;
  font-weight: 500;
  text-transform: none;
  border: 1px solid #6ee7b7;
  color: #059669;
  padding: 2px 8px;
  border-radius: 9999px;
}

.explain-table {
  margin-bottom: 12px;
}

.explain-summary .t-row {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  font-size: 13px;
}

.explain-summary .t-final {
  font-weight: 700;
  font-size: 16px;
  border-top: 1px solid #d1d5db;
  padding-top: 8px;
}
</style>
